/**
 * Sudo (Admin Impersonation) Context
 *
 * Allows admins to "view as" a specific viewer user, seeing their
 * dashboards and embed sessions with the viewer's permissions.
 *
 * State persists in sessionStorage so page refreshes don't lose sudo mode.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from '../services/api';

interface SudoContextType {
  sudoUser: User | null;
  isSudoMode: boolean;
  startSudo: (user: User) => void;
  stopSudo: () => void;
}

const SudoContext = createContext<SudoContextType>({
  sudoUser: null,
  isSudoMode: false,
  startSudo: () => {},
  stopSudo: () => {},
});

const STORAGE_KEY = 'sudo_user';

function loadSudoUser(): User | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function SudoProvider({ children }: { children: ReactNode }) {
  const [sudoUser, setSudoUser] = useState<User | null>(loadSudoUser);
  const queryClient = useQueryClient();

  const startSudo = useCallback((user: User) => {
    setSudoUser(user);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    queryClient.invalidateQueries({ queryKey: ['dashboards'] });
  }, [queryClient]);

  const stopSudo = useCallback(() => {
    setSudoUser(null);
    sessionStorage.removeItem(STORAGE_KEY);
    queryClient.invalidateQueries({ queryKey: ['dashboards'] });
  }, [queryClient]);

  return (
    <SudoContext.Provider value={{ sudoUser, isSudoMode: sudoUser !== null, startSudo, stopSudo }}>
      {children}
    </SudoContext.Provider>
  );
}

export function useSudo() {
  return useContext(SudoContext);
}
